import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RevenueTab, { RevenueSubTab } from './RevenueTab';
import OnboardingTab from './OnboardingTab';
import CohortsTab from './CohortsTab';
import AcquisitionTab from './AcquisitionTab';
import DailyReportTab from './DailyReportTab';
import BackupsTab, { fetchBackupStatus, BackupStatus, STATUS_COLOR } from './BackupsTab';
import AICostsTab from './AICostsTab';
import CancellationsTab from './CancellationsTab';
import PushLog from './PushLog';
import EmailTab from './EmailTab';
import ActiveSessions from './ActiveSessions';
import SharesTab from './SharesTab';
import ReminderFunnel from './ReminderFunnel';

import { API_BASE, API_EXTRA_HEADERS } from '../apiBase';
const TOKEN_KEY = 'gardeno_admin_token'; // now holds the JWT session token

// Auth header for admin requests — the JWT session from /admin/login.
export const authHeaders = (tok: string): Record<string, string> => ({
  ...API_EXTRA_HEADERS,
  Authorization: `Bearer ${tok}`,
});

interface Stats {
  total_users: number;
  guest_users: number;
  real_users: number;
  new_users_24h: number;
  new_users_7d: number;
  active_users_24h: number;
  active_users_7d: number;
  total_scans: number;
  scans_24h: number;
  scans_7d: number;
  paid_subscribers: number;
  comp_subscribers: number;
  trials_started_total: number;
  active_trials: number;
  paid_users: number;
  cancelled_total: number;
  paid_active: number;
  paid_cancelled: number;
  paid_lapsed: number;
  ever_paid: number;
  active_subs_total: number;
  shares_total: number;
  shares_visible: number;
  sharers_total: number;
  shares_24h: number;
  shares_7d: number;
  last_user_at: string | null;
  last_scan_at: string | null;
}

interface UserRow {
  email: string;
  name: string;
  provider: string;
  plan: string;
  period: string;
  cancelled: boolean;
  is_guest: boolean;
  device_id: string;
  country: string;
  city: string;
  created_at: string;
  trial_started_at?: string | null;
  cancelled_at?: string | null;
  last_scan_at?: string | null;
  scan_count: number;
  share_count: number;
  last_shared_at?: string | null;
  push_enabled: boolean;
  is_tester?: boolean;
}

// Preset views for the users table: each sets an implicit filter + sort and
// decides which date the last column shows. Client-side — the list is small.
const USER_VIEWS = [
  { key: 'all', label: 'All users', dateLabel: 'Created' },
  { key: 'cancelled', label: 'Recently cancelled', dateLabel: 'Cancelled' },
  { key: 'trials', label: 'New trials', dateLabel: 'Trial started' },
  { key: 'scanners', label: 'Recent scanners', dateLabel: 'Last scan' },
  { key: 'topscanners', label: 'Most scans', dateLabel: 'Last scan' },
  { key: 'sharers', label: 'Photo sharing', dateLabel: 'Last shared' },
  { key: 'push', label: 'Push enabled', dateLabel: 'Created' },
] as const;
type UserViewKey = typeof USER_VIEWS[number]['key'];

const PAGE_SIZE = 50;

// Page numbers to show, collapsing the middle with an ellipsis for long lists:
// e.g. 1 … 4 5 6 … 20
function pageTokens(cur: number, total: number): (number | '…')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: (number | '…')[] = [1];
  const start = Math.max(2, cur - 1);
  const end = Math.min(total - 1, cur + 1);
  if (start > 2) out.push('…');
  for (let p = start; p <= end; p++) out.push(p);
  if (end < total - 1) out.push('…');
  out.push(total);
  return out;
}

const ts = (s?: string | null) => (s ? new Date(s).getTime() : 0);

// Relative time for activity columns: "2h ago", "3d ago", else dd/mm/yy.
function relTime(s?: string | null): string {
  if (!s) return '—';
  const diff = Date.now() - new Date(s).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 14) return `${days}d ago`;
  return new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

// "🇺🇸 San Francisco" / "🇺🇸 US" / "—"
function location(country: string, city: string): string {
  if (!country) return '—';
  const f = flag(country).split(' ')[0];
  return city ? `${f} ${city}` : `${f} ${country.toUpperCase()}`;
}

// ISO country code (e.g. "US") -> flag emoji + code, "—" if unknown.
// Written without string/array spread to stay ES5-safe for the CRA build.
function flag(code: string): string {
  if (!code || code.length !== 2) return '—';
  const cc = code.toUpperCase();
  const a = 0x1f1e6 + cc.charCodeAt(0) - 65;
  const b = 0x1f1e6 + cc.charCodeAt(1) - 65;
  return String.fromCodePoint(a, b) + ' ' + cc;
}

const PLAN_BADGE: Record<string, { bg: string; fg: string }> = {
  Paid: { bg: '#123524', fg: '#4ADE80' },
  Trial: { bg: '#3a2a12', fg: '#F5A623' },
  Comp: { bg: '#241f3d', fg: '#A78BFA' },
  Expired: { bg: '#2a1c1c', fg: '#c98a8a' },
  Free: { bg: '#1c1c1c', fg: '#8a8a8a' },
};

function fmtDate(ts: string | null): string {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return ts; }
}

const C = {
  bg: '#0a0a0b',
  sidebar: '#0f0f11',
  panel: '#141416',
  panelAlt: '#1a1a1d',
  border: '#262629',
  text: '#f2f2f3',
  sub: '#8a8a90',
  accent: '#2D9E5F',
};

// Sidebar nav items. `disabled` ones are placeholders for future tabs.
const NAV = [
  { key: 'dashboard', label: 'Dashboard', icon: '▦', disabled: false },
  { key: 'users', label: 'Users', icon: '○', disabled: false,
    subs: USER_VIEWS.map((v) => ({ key: v.key, label: v.label })) },
  // Everything we send to users, and how it lands: push composer, care
  // reminder delivery, email. Named "Engagement" rather than "Messaging"
  // because it covers the reporting side, not just composing.
  { key: 'messaging', label: 'Engagement', icon: '✉', disabled: false,
    subs: [
      // Push delivery and care reminders are one story — both are
      // notifications, and the reminder funnel is the evidence for whether
      // sending them works at all. Email gets its own view.
      { key: 'sessions', label: 'Active sessions' },
      // What users send outward, and the moderation handle for it. Sits here
      // rather than under Data Analytics because it needs acting on, not
      // just reading.
      { key: 'shares', label: 'Shares' },
      { key: 'push', label: 'Notifications' },
      { key: 'email', label: 'Email' },
    ] },
  { key: 'revenue', label: 'Revenue', icon: '◍', disabled: false,
    subs: [{ key: 'overview', label: 'Overview' }, { key: 'upcoming', label: 'Upcoming' }, { key: 'past', label: 'Past' }] },
  { key: 'onboarding', label: 'Data Analytics', icon: '◔', disabled: false,
    subs: [{ key: 'daily', label: 'Daily report' }, { key: 'cancellations', label: 'Cancellations' }, { key: 'funnel', label: 'Onboarding funnel' }, { key: 'cohorts', label: 'Intent cohorts' }, { key: 'acquisition', label: 'Acquisition' }] },
  { key: 'aicosts', label: 'AI costs', icon: '◈', disabled: false },
  { key: 'backups', label: 'Backups', icon: '⛁', disabled: false },
  { key: 'scans', label: 'Scans', icon: '◎', disabled: true },
];

function Admin() {
  const [token, setToken] = useState<string>(() => sessionStorage.getItem(TOKEN_KEY) || '');
  const [username, setUsername] = useState('');
  const [input, setInput] = useState(''); // password field
  const [loggingIn, setLoggingIn] = useState(false);
  const [stats, setStats] = useState<Stats | null>(null);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('All');
  const [userView, setUserView] = useState<UserViewKey>('all');
  const [offset, setOffset] = useState(0);
  const [userTotal, setUserTotal] = useState(0);
  const [showTesters, setShowTesters] = useState(false);
  const [tab, setTab] = useState('dashboard');
  const [revSub, setRevSub] = useState<RevenueSubTab>('overview');
  const [obSub, setObSub] = useState<'daily' | 'cancellations' | 'funnel' | 'cohorts' | 'acquisition'>('daily');
  const [engSub, setEngSub] = useState<'push' | 'sessions' | 'shares' | 'email'>('sessions');
  const [pushLogKey, setPushLogKey] = useState(0);
  const [backupStatus, setBackupStatus] = useState<BackupStatus | null>(null);
  const [backupAlertDismissed, setBackupAlertDismissed] = useState(false);


  // Push composer state
  const [pushTarget, setPushTarget] = useState('all');
  const [pushEmail, setPushEmail] = useState('');
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushURL, setPushURL] = useState('');
  const [pushSending, setPushSending] = useState(false);
  const [pushResult, setPushResult] = useState<string | null>(null);

  // Clicking a user opens the full detail page (keyed by email).
  const navigate = useNavigate();
  function openUser(u: UserRow) {
    navigate(`/admin/user/${encodeURIComponent(u.email)}`);
  }

  // Responsive: stack the layout on narrow screens.
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 760);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Backup health drives a panel-wide banner, so it is fetched once the admin
  // is authenticated regardless of which tab is open, and refreshed every 10
  // minutes. Failures are swallowed: a backup-status outage must never block
  // the rest of the dashboard.
  useEffect(() => {
    if (!token) return;
    let alive = true;
    const poll = () => {
      fetchBackupStatus(token)
        .then((st) => { if (alive) setBackupStatus(st); })
        .catch(() => {});
    };
    poll();
    const t = setInterval(poll, 10 * 60 * 1000);
    return () => { alive = false; clearInterval(t); };
  }, [token]);

  async function load(tok: string) {
    setLoading(true);
    setError(null);
    try {
      const statsRes = await fetch(`${API_BASE}/admin/stats`, { headers: authHeaders(tok) });
      if (statsRes.status === 403 || statsRes.status === 401) {
        setError('Session expired. Please sign in again.');
        sessionStorage.removeItem(TOKEN_KEY);
        setToken('');
        return;
      }
      if (!statsRes.ok) { setError(`Server error (${statsRes.status}).`); return; }
      const sd = await statsRes.json();
      setStats(sd.data ?? sd);
    } catch {
      setError('Could not reach the server.');
    } finally {
      setLoading(false);
    }
  }

  // Users are fetched server-side with search/filter/sort/pagination applied.
  const loadUsers = React.useCallback(async (tok: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        search, plan: planFilter, view: userView,
        limit: String(PAGE_SIZE), offset: String(offset),
        include_testers: String(showTesters),
      }).toString();
      const res = await fetch(`${API_BASE}/admin/users?${params}`, { headers: authHeaders(tok) });
      if (res.status === 401 || res.status === 403) {
        setError('Session expired. Please sign in again.');
        sessionStorage.removeItem(TOKEN_KEY); setToken(''); return;
      }
      if (res.ok) {
        const ud = await res.json();
        const d = ud.data ?? ud;
        setUsers(d.rows ?? []);
        setUserTotal(d.total ?? 0);
      }
    } catch {
      setError('Could not reach the server.');
    } finally {
      setLoading(false);
    }
  }, [search, planFilter, userView, offset, showTesters]);

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Reset to the first page whenever the search / filter / view changes.
  useEffect(() => { setOffset(0); }, [search, planFilter, userView, showTesters]);

  // Reload the user list (debounced so typing in search doesn't spam the API).
  useEffect(() => {
    if (!token) return;
    const t = setTimeout(() => loadUsers(token), 250);
    return () => clearTimeout(t);
  }, [token, loadUsers]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const u = username.trim();
    const p = input;
    if (!u || !p) return;
    setLoggingIn(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/login`, {
        method: 'POST',
        headers: { ...API_EXTRA_HEADERS, 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: u, password: p }),
      });
      if (res.status === 401) { setError('Invalid username or password.'); return; }
      if (!res.ok) { setError(`Server error (${res.status}).`); return; }
      const d = await res.json();
      const jwt = d.data?.token ?? d.token;
      if (!jwt) { setError('Login failed.'); return; }
      sessionStorage.setItem(TOKEN_KEY, jwt);
      setInput('');
      setToken(jwt);
    } catch {
      setError('Could not reach the server.');
    } finally {
      setLoggingIn(false);
    }
  }

  async function sendPush() {
    if (!pushTitle.trim() || !pushBody.trim()) { setPushResult('Title and message are required.'); return; }
    const target = pushTarget === 'user' ? pushEmail.trim() : 'all';
    if (pushTarget === 'user' && !target) { setPushResult('Enter a user email.'); return; }
    setPushSending(true);
    setPushResult(null);
    try {
      const res = await fetch(`${API_BASE}/admin/push`, {
        method: 'POST',
        headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify({ target, title: pushTitle.trim(), body: pushBody.trim(), url: pushURL.trim() }),
      });
      if (res.status === 401) { sessionStorage.removeItem(TOKEN_KEY); setToken(''); return; }
      if (!res.ok) { setPushResult(`Failed (${res.status}).`); return; }
      const d = await res.json();
      const r = d.data ?? d;
      setPushResult(`Sent to ${r.sent}/${r.total} device(s).${r.failed ? ` ${r.failed} failed.` : ''}`);
      setPushTitle(''); setPushBody('');
      // Pull the log again so the send appears immediately.
      setPushLogKey((k) => k + 1);
    } catch {
      setPushResult('Could not reach the server.');
    } finally {
      setPushSending(false);
    }
  }

  // Stable so EmailTab's load callback (which depends on it) doesn't refire
  // every render.
  const onEmailUnauthorized = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken('');
  }, []);

  function logout() {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(''); setStats(null); setUsers([]); setInput(''); setUsername('');
  }

  // Filtering, sorting and pagination now happen server-side; the rows come
  // back ready to render.
  const filtered = users;

  // The date the last column shows depends on the selected view.
  const viewMeta = USER_VIEWS.find((v) => v.key === userView) ?? USER_VIEWS[0];
  const viewDate = (u: UserRow): string | null | undefined => {
    switch (userView) {
      case 'cancelled': return u.cancelled_at;
      case 'trials': return u.trial_started_at;
      case 'scanners':
      case 'topscanners': return u.last_scan_at;
      case 'sharers': return u.last_shared_at;
      case 'push': return u.created_at;
      default: return u.created_at;
    }
  };

  // ---------- login ----------
  if (!token) {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, color: C.text, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '-apple-system, system-ui, sans-serif' }}>
        <div style={{ width: 360, padding: 32, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src="/images/app-icon.png" alt="Gardeno" style={{ width: 40, height: 40, borderRadius: 10 }} />
            <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>Gardeno Admin</h1>
          </div>
          <p style={{ color: C.sub, marginTop: 14, fontSize: 14 }}>Sign in to continue.</p>
          <form onSubmit={submit} style={{ marginTop: 18 }}>
            <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username" autoFocus autoComplete="username"
              style={{ width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.panelAlt, color: C.text, fontSize: 15 }} />
            <input type="password" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Password" autoComplete="current-password"
              style={{ width: '100%', boxSizing: 'border-box', marginTop: 10, padding: '12px 14px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.panelAlt, color: C.text, fontSize: 15 }} />
            <button type="submit" disabled={loggingIn} style={{ width: '100%', marginTop: 12, background: C.accent, color: '#fff', border: 'none', borderRadius: 10, padding: '12px', fontWeight: 700, fontSize: 15, cursor: 'pointer', opacity: loggingIn ? 0.7 : 1 }}>{loggingIn ? 'Signing in…' : 'Sign In'}</button>
          </form>
          {error && <p style={{ color: '#F87171', marginTop: 14, fontSize: 14 }}>{error}</p>}
        </div>
      </div>
    );
  }

  const statCards = stats ? [
    { label: 'Total Users', value: stats.total_users, sub: `${stats.real_users} accounts · ${stats.guest_users} guests` },
    { label: 'Trials Started', value: stats.trials_started_total, sub: 'all time' },
    { label: 'Active Trials', value: stats.active_trials, sub: 'auto-renew on' },
    // The money-grounded subscriber picture: who is live right now, who is
    // actually being billed, and who paid then churned.
    { label: 'Active Subs', value: stats.active_subs_total, sub: `${stats.active_trials} trial · ${stats.paid_active} paid` },
    // Ever Paid comes from the revenue events themselves, not from adding the
    // cards below. Those describe a current state, and a payer can sit outside
    // all of them: someone whose card was declined never cancelled, so once
    // their access lapses they are neither active nor cancelled. Summing the
    // buckets is what made this read 14 when 15 people had actually paid.
    { label: 'Ever Paid', value: stats.ever_paid, sub: 'people Apple has charged · all time' },
    { label: 'Paid & Active', value: stats.paid_active, sub: 'charged · auto-renew on' },
    { label: 'Paid & Cancelled', value: stats.paid_cancelled, sub: 'churned after paying' },
    { label: 'Paid & Lapsed', value: stats.paid_lapsed, sub: 'billing failed · never cancelled' },
    { label: 'Cancelled', value: stats.cancelled_total, sub: 'all time · incl. trials' },
    { label: 'Active (24h)', value: stats.active_users_24h, sub: `${stats.active_users_7d} this week` },
    { label: 'Total Scans', value: stats.total_scans, sub: `${stats.scans_24h} today · ${stats.scans_7d} this week` },
    { label: 'New Users (7d)', value: stats.new_users_7d, sub: `${stats.new_users_24h} today` },
    // Plant Map. Both cards count only what is live on the map (moderated-out
    // shares excluded), and use rolling windows like the Active/Scans cards.
    { label: 'Plants Shared', value: stats.shares_visible, sub: `${stats.sharers_total} sharers` },
    { label: 'Shared (24h)', value: stats.shares_24h, sub: `${stats.shares_7d} this week` },
  ] : [];

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: '-apple-system, system-ui, sans-serif', display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
      {/* ---------- SIDEBAR (top bar on mobile) ---------- */}
      <aside style={{
        width: isMobile ? '100%' : 230,
        minHeight: isMobile ? 'auto' : '100vh',
        background: C.sidebar, borderRight: isMobile ? 'none' : `1px solid ${C.border}`,
        borderBottom: isMobile ? `1px solid ${C.border}` : 'none',
        padding: isMobile ? '14px 16px' : '22px 14px',
        position: isMobile ? 'static' : 'sticky', top: 0, alignSelf: 'flex-start', boxSizing: 'border-box',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: isMobile ? '0 0 12px' : '0 8px 22px' }}>
          <img src="/images/app-icon.png" alt="Gardeno" style={{ width: 34, height: 34, borderRadius: 9 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 16, lineHeight: 1 }}>Gardeno</div>
            <div style={{ fontSize: 11, color: C.sub, marginTop: 3 }}>Admin</div>
          </div>
          {isMobile && (
            <button onClick={logout} style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 14px', color: '#F87171', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Log out</button>
          )}
        </div>

        <nav style={{ display: 'flex', flexDirection: isMobile ? 'row' : 'column', gap: isMobile ? 6 : 2, overflowX: isMobile ? 'auto' : 'visible' }}>
          {NAV.map((n) => {
            const active = tab === n.key;
            return (
              <React.Fragment key={n.key}>
              <button
                onClick={() => !n.disabled && setTab(n.key)}
                disabled={n.disabled}
                style={{
                  display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12, width: isMobile ? 'auto' : '100%', textAlign: 'left',
                  padding: isMobile ? '8px 14px' : '10px 12px', borderRadius: 9,
                  border: isMobile ? `1px solid ${C.border}` : 'none', cursor: n.disabled ? 'default' : 'pointer',
                  background: active ? C.panelAlt : 'transparent',
                  color: n.disabled ? '#4a4a4f' : active ? C.text : C.sub,
                  fontSize: 14, fontWeight: active ? 700 : 500, whiteSpace: 'nowrap', flex: '0 0 auto',
                }}
              >
                <span style={{ fontSize: 15, width: 18, textAlign: 'center', color: active ? C.accent : 'inherit' }}>{n.icon}</span>
                {n.label}
                {n.subs && <span style={{ marginLeft: 'auto', fontSize: 11, opacity: 0.7 }}>{active ? '▾' : '▸'}</span>}
                {n.disabled && !isMobile && <span style={{ marginLeft: 'auto', fontSize: 10, color: '#4a4a4f' }}>soon</span>}
              </button>
              {n.subs && active && (
                <div style={{
                  display: 'flex', flexDirection: isMobile ? 'row' : 'column', gap: isMobile ? 6 : 1,
                  marginTop: isMobile ? 6 : 4, marginBottom: isMobile ? 0 : 6, flexWrap: 'wrap',
                  marginLeft: isMobile ? 0 : 20, paddingLeft: isMobile ? 0 : 12,
                  borderLeft: isMobile ? 'none' : `1.5px solid ${C.border}`,
                }}>
                  {n.subs.map((sub) => {
                    const subActive = n.key === 'revenue' ? revSub === sub.key
                      : n.key === 'onboarding' ? obSub === sub.key
                      : n.key === 'messaging' ? engSub === sub.key
                      : userView === sub.key;
                    return (
                      <button
                        key={sub.key}
                        onClick={() => {
                          if (n.key === 'revenue') setRevSub(sub.key as RevenueSubTab);
                          else if (n.key === 'onboarding') setObSub(sub.key as 'daily' | 'cancellations' | 'funnel' | 'cohorts' | 'acquisition');
                          else if (n.key === 'messaging') setEngSub(sub.key as 'push' | 'sessions' | 'shares' | 'email');
                          else setUserView(sub.key as UserViewKey);
                        }}
                        style={{
                          position: 'relative', textAlign: 'left', width: isMobile ? 'auto' : '100%',
                          padding: isMobile ? '6px 12px' : '7px 12px', borderRadius: 8,
                          border: isMobile ? `1px solid ${subActive ? C.accent : C.border}` : 'none', cursor: 'pointer',
                          background: subActive ? (isMobile ? C.accent : C.panelAlt) : 'transparent',
                          color: subActive ? (isMobile ? '#fff' : C.accent) : C.sub,
                          fontSize: 13, fontWeight: subActive ? 700 : 500, whiteSpace: 'nowrap', flex: '0 0 auto',
                        }}
                      >
                        {!isMobile && (
                          <span style={{
                            position: 'absolute', left: -13, top: '50%', transform: 'translateY(-50%)',
                            width: 6, height: 6, borderRadius: '50%',
                            background: subActive ? C.accent : 'transparent',
                          }} />
                        )}
                        {sub.label}
                      </button>
                    );
                  })}
                </div>
              )}
              </React.Fragment>
            );
          })}
        </nav>

        {!isMobile && (
          <div style={{ marginTop: 'auto', paddingTop: 18 }}>
            <button onClick={logout} style={{ width: '100%', background: 'none', border: `1px solid ${C.border}`, borderRadius: 9, padding: '10px', color: '#F87171', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Log out</button>
          </div>
        )}
      </aside>

      {/* ---------- MAIN ---------- */}
      <main style={{ flex: 1, padding: isMobile ? '24px 16px' : '36px 32px', minWidth: 0 }}>
        {/* Backup alarm on the Dashboard. Dashboard is the landing tab, so a
            broken backup job is seen on every login without the banner
            following you around the rest of the panel. */}
        {tab === 'dashboard' && backupStatus && backupStatus.status !== 'ok' && !backupAlertDismissed && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            background: backupStatus.status === 'failed' ? 'rgba(248,113,113,0.10)' : 'rgba(251,191,36,0.10)',
            border: `1px solid ${STATUS_COLOR[backupStatus.status]}55`,
            borderLeft: `4px solid ${STATUS_COLOR[backupStatus.status]}`,
            borderRadius: 12, padding: '12px 16px', marginBottom: 20,
          }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: STATUS_COLOR[backupStatus.status], flex: '0 0 auto' }} />
            <strong style={{ fontSize: 14, color: STATUS_COLOR[backupStatus.status] }}>
              {backupStatus.status === 'failed' ? 'Database backup failing'
                : backupStatus.status === 'warning' ? 'Database backup overdue'
                : 'Backups not configured'}
            </strong>
            <span style={{ fontSize: 13.5, color: C.sub }}>{backupStatus.message}</span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button
                onClick={() => setTab('backups')}
                style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 7, padding: '6px 12px', cursor: 'pointer', color: C.text, fontSize: 12.5, fontWeight: 700 }}
              >
                View
              </button>
              <button
                onClick={() => setBackupAlertDismissed(true)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.sub, fontSize: 16, lineHeight: 1, padding: '0 4px' }}
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          </div>
        )}
        {/* shared header — not shown for revenue/onboarding (they render their own) */}
        {tab !== 'revenue' && tab !== 'onboarding' && tab !== 'backups' && tab !== 'aicosts' && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div>
              <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0 }}>{tab === 'users' ? 'User Management' : tab === 'messaging' ? 'Engagement' : 'Dashboard'}</h1>
              <p style={{ color: C.sub, marginTop: 6, fontSize: 15 }}>{tab === 'messaging'
                ? (engSub === 'push'
                  ? 'Whether notifications reach people, what they do with them, and one-off sends.'
                  : engSub === 'sessions'
                  ? 'Who is using the app right now, and who was on recently.'
                  : engSub === 'shares'
                  ? 'Plants users have made public, and the handle to take one down.'
                  : 'Write and send emails from usman@gardenoai.com, and everything sent so far.')
                : 'Live overview of users, scans, and subscriptions.'}</p>
            </div>
            {tab !== 'messaging' && <button onClick={() => { load(token); loadUsers(token); }} style={{ background: C.panelAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 18px', cursor: 'pointer', color: C.text, fontSize: 14 }}>Refresh</button>}
          </div>
        )}

        {loading && tab !== 'messaging' && tab !== 'revenue' && tab !== 'onboarding' && tab !== 'backups' && tab !== 'aicosts' && <p style={{ marginTop: 24, color: C.sub }}>Loading…</p>}
        {error && tab !== 'revenue' && tab !== 'onboarding' && tab !== 'backups' && tab !== 'aicosts' && <p style={{ color: '#F87171', marginTop: 16 }}>{error}</p>}

        {/* revenue tab (self-contained) */}
        {tab === 'aicosts' && <AICostsTab token={token} isMobile={isMobile} />}

        {tab === 'backups' && <BackupsTab token={token} isMobile={isMobile} />}

        {tab === 'revenue' && <RevenueTab token={token} isMobile={isMobile} subTab={revSub} />}

        {/* data analytics tab (self-contained) */}
        {tab === 'onboarding' && obSub === 'daily' && <DailyReportTab token={token} isMobile={isMobile} />}
        {tab === 'onboarding' && obSub === 'cancellations' && <CancellationsTab token={token} isMobile={isMobile} />}
        {tab === 'onboarding' && obSub === 'funnel' && <OnboardingTab token={token} isMobile={isMobile} />}
        {tab === 'onboarding' && obSub === 'cohorts' && <CohortsTab token={token} isMobile={isMobile} />}
        {tab === 'onboarding' && obSub === 'acquisition' && <AcquisitionTab token={token} isMobile={isMobile} />}

        {/* engagement → email: one-off sends from usman@ and the record of them */}
        {tab === 'messaging' && engSub === 'email' && (
          <EmailTab token={token} isMobile={isMobile} onUnauthorized={onEmailUnauthorized} />
        )}

        {/* engagement → notifications: care reminder delivery, then the composer */}
        {tab === 'messaging' && engSub === 'sessions' && (
          <ActiveSessions token={token} isMobile={isMobile} />
        )}

        {tab === 'messaging' && engSub === 'shares' && (
          <SharesTab token={token} isMobile={isMobile} />
        )}

        {tab === 'messaging' && engSub === 'push' && (
          <ReminderFunnel token={token} isMobile={isMobile} />
        )}

        {tab === 'messaging' && engSub === 'push' && (
          <PushLog token={token} isMobile={isMobile} refreshKey={pushLogKey} />
        )}

        {tab === 'messaging' && engSub === 'push' && (
          <div style={{ maxWidth: 560, marginTop: 32, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Send a push</div>
            <div style={{ fontSize: 12, color: C.sub, marginBottom: 18 }}>
              One-off announcement. Care reminders are scheduled on the device and are not sent from here.
            </div>
            <div style={{ fontSize: 13, color: C.sub, marginBottom: 6 }}>Send to</div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              {[{ k: 'all', l: 'All users' }, { k: 'user', l: 'Specific user' }].map((o) => (
                <button key={o.k} onClick={() => setPushTarget(o.k)} style={{ flex: 1, padding: '10px', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 600,
                  background: pushTarget === o.k ? C.panelAlt : 'transparent', color: pushTarget === o.k ? C.text : C.sub,
                  border: `1px solid ${pushTarget === o.k ? C.accent : C.border}` }}>{o.l}</button>
              ))}
            </div>
            {pushTarget === 'user' && (
              <>
                {/* Pick from the users who can actually receive a push (have a
                    registered device token) instead of typing an email blind. */}
                <select value={pushEmail} onChange={(e) => setPushEmail(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', marginBottom: 8, padding: '11px 14px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.panelAlt, color: pushEmail ? C.text : C.sub, fontSize: 14, cursor: 'pointer' }}>
                  <option value="">Select a user… ({users.filter((u) => u.push_enabled).length} push-enabled)</option>
                  {users.filter((u) => u.push_enabled)
                    .sort((a, b) => ts(b.last_scan_at) - ts(a.last_scan_at))
                    .map((u) => (
                      <option key={u.email} value={u.email}>
                        {(u.name || (u.is_guest ? 'Guest device' : u.email)) + ' — ' + u.plan + (u.scan_count > 0 ? ` · ${u.scan_count} scans` : '')}
                      </option>
                    ))}
                </select>
                {pushEmail && <div style={{ fontSize: 12, color: C.sub, marginBottom: 12 }}>Sending to: {pushEmail}</div>}
              </>
            )}
            <input value={pushTitle} onChange={(e) => setPushTitle(e.target.value)} placeholder="Notification title" maxLength={80}
              style={{ width: '100%', boxSizing: 'border-box', marginBottom: 12, padding: '11px 14px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.panelAlt, color: C.text, fontSize: 14 }} />
            <textarea value={pushBody} onChange={(e) => setPushBody(e.target.value)} placeholder="Message…" rows={4} maxLength={240}
              style={{ width: '100%', boxSizing: 'border-box', padding: '11px 14px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.panelAlt, color: C.text, fontSize: 14, resize: 'vertical', fontFamily: 'inherit' }} />
            {/* Optional: opened when the user taps the notification. Needs an
                app build with the url handler (2.1.3+); older builds just open
                the app. Use for App Store offer-code redemption links. */}
            <input value={pushURL} onChange={(e) => setPushURL(e.target.value)} placeholder="Link to open on tap (optional, https://…)" maxLength={500}
              style={{ width: '100%', boxSizing: 'border-box', marginTop: 12, padding: '11px 14px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.panelAlt, color: C.text, fontSize: 14 }} />
            <button onClick={sendPush} disabled={pushSending} style={{ marginTop: 16, width: '100%', background: C.accent, color: '#fff', border: 'none', borderRadius: 12, padding: '13px', fontWeight: 700, fontSize: 15, cursor: 'pointer', opacity: pushSending ? 0.7 : 1 }}>
              {pushSending ? 'Sending…' : pushTarget === 'all' ? 'Send to all users' : 'Send'}
            </button>
            {pushResult && <p style={{ marginTop: 14, fontSize: 14, color: pushResult.startsWith('Sent') ? '#4ADE80' : '#F87171' }}>{pushResult}</p>}
            <p style={{ marginTop: 16, fontSize: 12, color: C.sub, lineHeight: 1.5 }}>Only reaches users who granted notification permission. Keep it useful — Apple discourages spammy marketing pushes.</p>
          </div>
        )}

        {/* stat cards (dashboard tab) */}
        {tab === 'dashboard' && stats && (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 10, marginTop: 20 }}>
            {statCards.map((c) => (
              <div key={c.label} style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, color: C.sub, textTransform: 'uppercase', letterSpacing: 0.3 }}>{c.label}</div>
                <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4, lineHeight: 1 }}>{c.value}</div>
                <div style={{ fontSize: 11.5, color: C.sub, marginTop: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.sub}</div>
              </div>
            ))}
          </div>
        )}

        {/* users table (dashboard + users tabs) */}
        {(tab === 'dashboard' || tab === 'users') && users.length > 0 && (
          <>
            <div style={{ display: 'flex', gap: 12, marginTop: tab === 'users' ? 26 : 32, flexWrap: 'wrap', alignItems: 'center' }}>
              {tab === 'dashboard' && <h2 style={{ fontSize: 18, fontWeight: 800, margin: '0 8px 0 0' }}>Users</h2>}
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by email or name…"
                style={{ flex: 1, minWidth: 240, padding: '11px 16px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.panel, color: C.text, fontSize: 14 }} />
              <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}
                style={{ padding: '11px 16px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.panel, color: C.text, fontSize: 14, cursor: 'pointer' }}>
                {['All', 'Paid', 'Trial', 'Comp', 'Expired', 'Free'].map((p) => <option key={p} value={p}>{p === 'All' ? 'All Plans' : p}</option>)}
              </select>
              <button
                onClick={() => setShowTesters((v) => !v)}
                title="Apple App Review devices and internal test installs are hidden and excluded from totals by default."
                style={{
                  padding: '11px 16px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  border: `1px solid ${showTesters ? '#F5A623' : C.border}`,
                  background: showTesters ? '#3a2a12' : C.panel,
                  color: showTesters ? '#F5A623' : C.sub, whiteSpace: 'nowrap',
                }}>
                {showTesters ? '🧪 Testers shown' : 'Show testers'}
              </button>
            </div>

            {/* view presets: filter + sort in one tap */}
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              {USER_VIEWS.map((v) => {
                const active = userView === v.key;
                return (
                  <button key={v.key} onClick={() => setUserView(v.key)} style={{
                    background: active ? C.accent : C.panel,
                    color: active ? '#fff' : C.sub,
                    border: `1px solid ${active ? C.accent : C.border}`,
                    borderRadius: 100, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}>{v.label}</button>
                );
              })}
              <span style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: 12, color: C.sub }}>
                {userTotal === 0 ? '0 users'
                  : `${offset + 1}–${Math.min(offset + PAGE_SIZE, userTotal)} of ${userTotal}`}
              </span>
            </div>

            <div style={{ marginTop: 12, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: C.sub }}>
                      <th style={{ padding: '12px 10px', fontWeight: 600 }}>Email</th>
                      <th style={{ padding: '12px 10px', fontWeight: 600 }}>Name</th>
                      <th style={{ padding: '12px 10px', fontWeight: 600 }}>Plan</th>
                      <th style={{ padding: '12px 10px', fontWeight: 600 }}>Status</th>
                      <th style={{ padding: '12px 10px', fontWeight: 600 }}>Location</th>
                      <th style={{ padding: '12px 10px', fontWeight: 600 }}>Scans</th>
                      <th style={{ padding: '12px 10px', fontWeight: 600 }}>{viewMeta.dateLabel}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((u, i) => {
                      const badge = PLAN_BADGE[u.plan] || PLAN_BADGE.Free;
                      return (
                        <tr key={i} onClick={() => openUser(u)} style={{ borderTop: `1px solid ${C.border}`, cursor: 'pointer' }}>
                          <td style={{ padding: '12px 10px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {u.is_guest ? <span style={{ color: C.sub }}>Guest device</span> : u.email}
                          </td>
                          <td style={{ padding: '12px 10px', color: u.name ? C.text : C.sub }}>
                            {u.name || '—'}
                            {u.is_tester && <span title="Internal tester — excluded from stats" style={{ marginLeft: 7, background: '#3a2a12', color: '#F5A623', padding: '2px 7px', borderRadius: 100, fontSize: 11, fontWeight: 700 }}>🧪 Tester</span>}
                          </td>
                          <td style={{ padding: '12px 10px' }}>
                            <span style={{ background: badge.bg, color: badge.fg, padding: '4px 12px', borderRadius: 100, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {u.plan}{u.period ? ` · ${u.period}` : ''}
                            </span>
                          </td>
                          <td style={{ padding: '12px 10px' }}>
                            {u.plan === 'Expired' ? (
                              <span style={{ background: '#2a1c1c', color: '#c98a8a', padding: '4px 12px', borderRadius: 100, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>Expired</span>
                            ) : u.cancelled ? (
                              <span style={{ background: '#3a1414', color: '#F87171', padding: '4px 12px', borderRadius: 100, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>Cancelled</span>
                            ) : (u.plan === 'Paid' || u.plan === 'Trial' || u.plan === 'Comp') ? (
                              <span style={{ color: '#4ADE80', fontSize: 13, fontWeight: 600 }}>Active</span>
                            ) : (
                              <span style={{ color: C.sub }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: '12px 10px', color: u.country ? C.text : C.sub, whiteSpace: 'nowrap' }}>{location(u.country, u.city)}</td>
                          <td style={{ padding: '12px 10px', whiteSpace: 'nowrap' }}>
                            {userView === 'sharers' ? (
                              <span style={{ color: C.text, fontWeight: 600 }}>{u.share_count}<span style={{ color: C.sub, fontWeight: 400 }}> shared</span></span>
                            ) : u.scan_count > 0 ? (
                              <span style={{ color: C.text, fontWeight: 600 }}>{u.scan_count}<span style={{ color: C.sub, fontWeight: 400 }}> · {relTime(u.last_scan_at)}</span></span>
                            ) : (
                              <span style={{ color: C.sub }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: '12px 10px', color: C.sub, whiteSpace: 'nowrap' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              {userView === 'all' ? fmtDate(u.created_at) : relTime(viewDate(u))}
                              {u.push_enabled && (
                                <button
                                  title="Send push notification"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPushTarget('user');
                                    setPushEmail(u.email);
                                    setTab('messaging');
                                  }}
                                  style={{ background: C.panelAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: '3px 9px', cursor: 'pointer', fontSize: 13, lineHeight: 1 }}
                                >🔔</button>
                              )}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                    {filtered.length === 0 && (
                      <tr><td colSpan={7} style={{ padding: '28px 18px', textAlign: 'center', color: C.sub }}>No users match your filters.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* PAGINATION — numbered, at the very bottom */}
            {userTotal > PAGE_SIZE && (() => {
              const totalPages = Math.ceil(userTotal / PAGE_SIZE);
              const cur = Math.floor(offset / PAGE_SIZE) + 1;
              const go = (p: number) => setOffset((Math.min(Math.max(1, p), totalPages) - 1) * PAGE_SIZE);
              const navBtn = (label: string, page: number, disabled: boolean) => (
                <button onClick={() => !disabled && go(page)} disabled={disabled}
                  style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 12px',
                    fontSize: 13, fontWeight: 600, cursor: disabled ? 'default' : 'pointer', color: disabled ? '#4a4a4f' : C.text }}>
                  {label}
                </button>
              );
              return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: 22, flexWrap: 'wrap' }}>
                  {navBtn('‹ Prev', cur - 1, cur === 1)}
                  {pageTokens(cur, totalPages).map((t, i) =>
                    t === '…'
                      ? <span key={`e${i}`} style={{ color: C.sub, padding: '0 4px' }}>…</span>
                      : (
                        <button key={t} onClick={() => go(t)}
                          style={{
                            minWidth: 36, padding: '7px 10px', borderRadius: 8, fontSize: 13, fontWeight: t === cur ? 800 : 600,
                            cursor: 'pointer', border: `1px solid ${t === cur ? C.accent : C.border}`,
                            background: t === cur ? C.accent : C.panel, color: t === cur ? '#0c1a12' : C.text,
                          }}>
                          {t}
                        </button>
                      )
                  )}
                  {navBtn('Next ›', cur + 1, cur === totalPages)}
                </div>
              );
            })()}
          </>
        )}
      </main>

    </div>
  );
}

export default Admin;
